/**
 * Auth input primitives: a PIN field and an OTP field (Module 01 component inventory).
 *
 * Both are the same idea, a fixed number of single-digit cells, driven by one real input sitting
 * invisibly on top so the OS keyboard, autofill (SMS one-time-code), and paste all work. The cells are
 * just a display. This beats N separate inputs, which fight autofill and paste and are a well-known
 * source of mobile bugs.
 *
 * Accessibility and iOS: the real input carries the label and a 16px font size, so Safari never zooms
 * the viewport when it gains focus (the <16px zoom trap).
 */

import { useEffect, useRef } from 'react';

function CodeField({
  value,
  onChange,
  length,
  mask = false,
  autoFocus = false,
  autoComplete,
  ariaLabel,
  invalid = false,
}: {
  value: string;
  onChange: (v: string) => void;
  length: number;
  mask?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  ariaLabel: string;
  invalid?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  return (
    <div className="relative w-full select-none">
      <input
        ref={ref}
        inputMode="numeric"
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        aria-invalid={invalid}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, length))}
        maxLength={length}
        // Invisible, but a real focusable input over the cells: taps focus it, the keyboard appears,
        // paste and SMS autofill land here. 16px so iOS never zooms.
        className="absolute inset-0 z-10 h-full w-full cursor-pointer text-base opacity-0"
      />
      <div className="pointer-events-none flex justify-center gap-2" aria-hidden>
        {Array.from({ length }).map((_, i) => {
          const active = i === value.length;
          const filled = i < value.length;
          return (
            <div
              key={i}
              className={`flex h-12 w-11 items-center justify-center rounded-field border bg-surface text-lg font-semibold text-ink transition ${
                invalid
                  ? 'border-danger'
                  : active
                    ? 'border-primary ring-2 ring-primary/25'
                    : filled
                      ? 'border-border-strong'
                      : 'border-border'
              }`}
            >
              {filled ? (
                mask ? (
                  <span className="h-2.5 w-2.5 rounded-full bg-ink" />
                ) : (
                  value[i]
                )
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PinField({
  value,
  onChange,
  length = 4,
  autoFocus,
  invalid,
  ariaLabel = 'PIN',
  confirm = false,
}: {
  value: string;
  onChange: (v: string) => void;
  length?: number;
  autoFocus?: boolean;
  invalid?: boolean;
  ariaLabel?: string;
  /** A confirm field autocompletes as a new password rather than the current one. */
  confirm?: boolean;
}) {
  return (
    <CodeField
      value={value}
      onChange={onChange}
      length={length}
      mask
      autoFocus={autoFocus}
      invalid={invalid}
      ariaLabel={ariaLabel}
      autoComplete={confirm ? 'new-password' : 'current-password'}
    />
  );
}

export function OtpField({
  value,
  onChange,
  autoFocus,
  invalid,
  ariaLabel = 'One-time code',
}: {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  invalid?: boolean;
  ariaLabel?: string;
}) {
  return (
    <CodeField
      value={value}
      onChange={onChange}
      length={6}
      autoFocus={autoFocus}
      invalid={invalid}
      ariaLabel={ariaLabel}
      autoComplete="one-time-code"
    />
  );
}
